'use client';

import { useRef } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { NavigationMenu } from '@/components/Navigation/NavigationMenu';
import { MobileMenu } from '@/components/Navigation/MobileMenu';
import { LPThemeToggle } from '@/components/v2/LPThemeToggle';
import { HeaderCtaFx, HeaderCtaSweep, HEADER_CTA_FX_CSS } from '@/components/Navigation/HeaderCtaFx';
import { TryItLink } from '@/components/Navigation/TryItLink';
import { START_HERE_PATH, StartHereCta, StartHereModal } from '@/components/widgets/StartHere/StartHereModal';
import logo from '@/assets/leadpops-mortgage-marketing-logo.webp';

interface NavigationItem {
  label: string;
  href: string;
  type?: 'mega';
  dropdown?: {
    label: string;
    href: string;
    description: string;
  }[];
  columns?: any[];
  cta?: any;
}

interface NavigationData {
  mainNav: NavigationItem[];
  footerNav: {
    solutions: { label: string; href: string }[];
    resources: { label: string; href: string }[];
    company: { label: string; href: string }[];
    legal: { label: string; href: string }[];
  };
}

interface ClientNavigationProps {
  navData: NavigationData;
}

export const ClientNavigation = ({ navData }: ClientNavigationProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // the Start Here page (hidden preview): the header's key opens the chooser instead of going to the demo request
  const startHere = usePathname() === START_HERE_PATH;
  const KEY = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:shadow-elevation-2 h-10 py-2 bg-white border-2 border-[#ff34a7] text-[#ff34a7] hover:bg-[#ff34a7] hover:text-white font-semibold px-6';
  const key = startHere
    ? <StartHereCta className={KEY}><HeaderCtaSweep /></StartHereCta>
    : <Link href="/mortgage-lead-generation-demo" className={KEY}>Book Strategy Call<HeaderCtaSweep /></Link>;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-elevation-1">
      {startHere && <StartHereModal />}
      <div ref={containerRef} className="container-header">
        <div className="flex items-center w-full h-16">
          {/* Logo - Left */}
          <div className="flex-shrink-0">
            <Link href="/">
              <Image
                src={logo}
                alt="LeadPops Mortgage Lead Generation Platform Logo"
                width={140}
                height={51}
                sizes="140px"
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Center with flex-1 for space */}
          <div className="flex-1 flex justify-center">
            <NavigationMenu items={navData.mainNav} containerRef={containerRef} />
          </div>

          {/* Right - Responsive CTAs */}
          <style dangerouslySetInnerHTML={{ __html: HEADER_CTA_FX_CSS }} />
          <div className="flex-shrink-0 flex items-center space-x-4">
            {/* Desktop: Demo Only (plus the light/dark switch on v2-design pages) */}
            <div className="hidden xl:flex items-center gap-3">
              <LPThemeToggle />
              <TryItLink className="lp-tryit inline-flex items-center h-10 px-3 rounded-full text-sm font-semibold whitespace-nowrap text-[color:var(--sk-pinkt,#c8117f)] hover:underline underline-offset-4" />
              <HeaderCtaFx>{key}</HeaderCtaFx>
            </div>

            {/* Tablet: Demo + Hamburger */}
            <div className="hidden md:flex xl:hidden items-center space-x-4">
              <LPThemeToggle />
              <HeaderCtaFx>{key}</HeaderCtaFx>
              <MobileMenu items={navData.mainNav} startHere={startHere} />
            </div>

            {/* Mobile: Demo + Hamburger */}
            <div className="flex md:hidden items-center space-x-4">
              <HeaderCtaFx>{key}</HeaderCtaFx>
              <MobileMenu items={navData.mainNav} startHere={startHere} />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

